import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { Content } from '@monorepo/golab-generated'
import { CommonModule } from '@angular/common'
import { MatIconModule } from '@angular/material/icon'
import { MatSelectModule } from '@angular/material/select'
import { LabProvider } from '@monorepo/next-lab'
import { AutoResizeElementService, IlsModelPartsService } from '../../services'
import { TranslatePipe } from '@ngx-translate/core'
import { ANGULAR_COMMON_CONFIG } from '../../models'

const gatewayLabUrl = 'https://gateway.golabz.eu'
const maxDesiredHeight = 2000

@Component({
  selector: 'app-iframe-app-and-lab',
  templateUrl: './iframe-app-and-lab.component.html',
  styleUrls: ['./iframe-app-and-lab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe, CommonModule, MatIconModule, MatSelectModule],
})
export class IframeAppAndLabComponent implements AfterViewInit, OnDestroy {
  private readonly sanitizer = inject(DomSanitizer)
  element = inject(ElementRef)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly angularCommonConfig = inject(ANGULAR_COMMON_CONFIG)

  labProvider = ''
  showInIframe = true
  saveAppUrl: SafeResourceUrl = ''
  appUrl = ''
  gateWayLab = false
  originalLabUrl = ''
  setSize = false
  width = 1280
  height = 720
  fixedHeight = '650px'
  private autoResizeElementService: AutoResizeElementService | undefined
  private readonly serverProxyBaseUrl: string
  private readonly serverProxyUrl: string
  private initFinished = false

  @Input() i18nKey = ''
  @Input() player = false

  private _editor = false
  @Input() set editor(value: boolean) {
    this._editor = value
  }

  private _ilsModelService: IlsModelPartsService | undefined
  @Input() set ilsModelService(ilsModelService: IlsModelPartsService) {
    this._ilsModelService = ilsModelService
  }

  get ilsModelService(): IlsModelPartsService {
    if (!this._ilsModelService) {
      throw new Error('ilsModelService is not set')
    }
    return this._ilsModelService
  }

  constructor() {
    this.setAppUrl('')
    this.serverProxyBaseUrl = this.getServerProxyBaseUrl()
    this.serverProxyUrl = `${this.serverProxyBaseUrl}url/`
    console.log('serverProxyBaseUrl', this.serverProxyBaseUrl)
  }

  private getServerProxyBaseUrl(): string {
    const rawServerURL = `${this.angularCommonConfig.serverUrl}/proxy/`
    if (rawServerURL.startsWith('http://') || rawServerURL.startsWith('https://')) {
      return rawServerURL
    }
    return location.origin + rawServerURL
  }

  private _content: Content | undefined

  get content(): Content | undefined {
    return this._content
  }

  @Input() set content(content: Content | undefined) {
    if (content) {
      // console.log('iframe-app-and-lab component content', this.initFinished)
      this._content = content
      if (this.initFinished) {
        this.setAppLabInfo(content)
      }
    }
  }

  ngAfterViewInit(): void {
    // console.log('iframe-app-and-lab component after view init')
    this.autoResizeElementService = new AutoResizeElementService(this.ilsModelService, (size) => {
      // console.log('newSize$', size)
      setTimeout(() => {
        this.width = size.width
        this.height = size.height
        // the setTimeout does not trigger the change detection in firefox, so we need to do it manually
        this.cdr.detectChanges()
      })
    })
    this.autoResizeElementService.setDesiredSize(1920, 1080)
    if (this._editor) this.autoResizeElementService.usePhaseToolbar()
    if (this._content) {
      if (this.player) {
        this.autoResizeElementService.setReferenceId(this._content._id)
      }
      this.setAppLabInfo(this._content)
    }
    this.setupListeningToIframe()
    this.initFinished = true
  }

  ngOnDestroy(): void {
    this.autoResizeElementService?.destroy()
  }

  private async setAppLabInfo(content?: Content) {
    if (!content?.url) {
      return
    }
    const isLab = content.contentType === 'lab'
    this.labProvider = content?.labProvider ?? 'other'
    const url = isLab ? this.getLabReplacementUrl(content.url, this.labProvider) : content.url
    const appUrl = await this.getAppUrl(url, this.labProvider)
    this.setAppUrl(appUrl)
  }

  private getLabReplacementUrl(url: string, labProvider: string): string {
    switch (labProvider) {
      case LabProvider.duffy:
      case LabProvider.chemCollective:
      case LabProvider.academo:
      case LabProvider.seilias:
      case LabProvider.walterFendt:
      case LabProvider.kcvs:
      case LabProvider.galileoAndEinstein:
      case LabProvider.amrita:
      case LabProvider.harshuEdu:
      case LabProvider.physicsaviary:
        return `${this.serverProxyBaseUrl}${labProvider}/${url}`
      case LabProvider.leoSiiman:
        return `${this.serverProxyUrl}${url}`
      default:
        return url
    }
  }

  private async getAppUrl(url: string, labProvider: string): Promise<string> {
    // console.log('getAppUrl', url, labProvider)
    this.originalLabUrl = ''
    this.setSize = false
    this.showInIframe = true
    if (!this.autoResizeElementService) {
      console.warn('autoResizeElementService is not set')
      return url
    }
    switch (labProvider) {
      case LabProvider.embed:
      case LabProvider.sadhanaPuntambekar:
      case LabProvider.raimundGirwidz:
      case LabProvider.astroUnlEdu:
        return url + `#resourceId=${this.content?._id}&lang=${this.ilsModelService.language$.value}`
      case LabProvider.phet:
        return this.getI18nPhetUrl(url)
      case 'concord':
        this.setSize = true
        this.autoResizeElementService.setDesiredSize(1050, 800)
        this.autoResizeElementService.setAutoResize(true)
        return await this.getI18nConcordUrl(url)
      case LabProvider.duffy:
      case LabProvider.academo:
      case LabProvider.walterFendt:
      case LabProvider.galileoAndEinstein:
      case LabProvider.harshuEdu:
        setTimeout(() => {
          this.originalLabUrl = this.getRealLabUrl(url)
        })
        // start with small height, as fixedHeight can be seen as the minimum height
        this.fixedHeight = '500px'
        return url
      case LabProvider.hbcse:
        this.fixedHeight = '730px'
        return url
      case LabProvider.cosci:
        this.fixedHeight = '880px'
        return url
      case LabProvider.kcvs:
        this.fixedHeight = '500px'
        return url
      case LabProvider.vascak:
        this.setSize = true
        this.autoResizeElementService.setDesiredSize(1280, 800)
        this.autoResizeElementService.setAutoResize(true)
        return url + `?l=${this.ilsModelService.language$.value}`
      case LabProvider.chemCollective:
        // the language can be specified as search parameter in the url (e.g. ?lang=es)
        // however, there are only solutions present in english
        return url + `?lang=en`
      case LabProvider.physicsaviary:
        this.setFixedHeightFromUrl()
        return url
      case LabProvider.virtualBiologyHtml5Lab:
        this.setSize = true
        this.autoResizeElementService.setAutoResize(true)
        return url
      case LabProvider.stAndrews:
        this.setSize = true
        this.autoResizeElementService.setDesiredSize(1198, 786)
        this.autoResizeElementService.setAutoResize(true)
        return url
      case LabProvider.seilias:
        this.setSize = true
        this.autoResizeElementService.setDesiredSize(1600, 1400)
        this.autoResizeElementService.setMinWidth(800)
        this.autoResizeElementService.setAutoResize(true)
        return url
      case LabProvider.amrita:
        this.fixedHeight = '560px'
        return url
      default:
        return url
    }
  }

  private getI18nPhetUrl(url: string): string {
    const languageCode = this.ilsModelService.language$.value
      .replace('-', '_')
      .replace('_tw', '_TW')
      .replace('pt', 'pt_BR')
    return url.replace('_en.', `_${languageCode}.`).replace('_all.', `_${languageCode}.`)
  }

  private async getI18nConcordUrl(url: string): Promise<string> {
    if (this.ilsModelService.language$.value === 'en') {
      return url
    }
    return this.findAndSetI18nConcordUrl(url, this.ilsModelService.language$.value)
  }

  private async findAndSetI18nConcordUrl(url: string, language: string): Promise<string> {
    const metadataUrl = url.replace('embeddable.html#', 'locales/metadata/')
    try {
      const response = await fetch(metadataUrl)
      // console.log('response', response)
      if (!response.ok) {
        return url
      }
      const metadata = await response.json()
      // console.log('metadata', metadata)
      if (metadata?.[language]) {
        return url.replace('embeddable.html#', `embeddable.html#locales/${language}/`)
      }
      return url
    } catch (e) {
      console.error(e)
      return url
    }
  }

  private getRealLabUrl(url: string): string {
    const duffyLabUrlPosition = url.indexOf('https', 1)
    return url.substring(duffyLabUrlPosition)
  }

  private setFixedHeightFromUrl() {
    const url = this.content?.url
    if (!url) {
      return
    }
    const heightName = '&height='
    const heightIndex = url?.lastIndexOf(heightName)
    if (heightIndex === -1) {
      return
    }
    const height = url?.substring(heightIndex + heightName.length)
    if (height) {
      this.setFixedHeight(parseInt(height) + 16)
    }
  }

  private setFixedHeight(height: number | string) {
    if (typeof height === 'number') {
      this.fixedHeight = height + 'px'
    } else if (height.endsWith('px')) {
      this.fixedHeight = height
    } else {
      this.fixedHeight = height + 'px'
    }
  }

  private setAppUrl(url: string) {
    // console.log('setAppUrl', url)
    if (this.appUrl !== url) {
      const iframe = this.getIFrame()
      if (iframe) {
        iframe.style.height = ''
      }
    }
    this.appUrl = url
    const urlParameters = this.appUrl ? this.getUrlParameters() : ''
    // console.log(this.appUrl, urlParameters)
    this.saveAppUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.addUrlParameters(this.appUrl, urlParameters))
    // console.log(this.saveAppUrl)
    this.gateWayLab = this.appUrl.startsWith(gatewayLabUrl)
  }

  private getIFrame(): HTMLIFrameElement | undefined {
    return this.element.nativeElement.querySelector('.height-sized-iframe') as HTMLIFrameElement
  }

  private addUrlParameters(url: string, parameters: string): string {
    if (!parameters) {
      return url
    }
    if (this.labProvider === LabProvider.galileoAndEinstein) {
      const connectionCharacter = url.includes('?') ? '&' : '?'
      return url + connectionCharacter + parameters
    }
    return url + '#' + parameters
  }

  private getUrlParameters(): string {
    switch (this.labProvider) {
      case LabProvider.other:
      case LabProvider.wulfranFortin:
        return ''
    }
    const urlParameters: string[] = []
    urlParameters.push(`contentId=${this.content?._id}`)
    urlParameters.push(`resourceId=${this.content?._id}`)
    urlParameters.push(`target=${window.location.origin}`)
    urlParameters.push(`lang=${this.ilsModelService.language$.value}`)
    const context = this.player ? 'ils' : 'graasp'
    urlParameters.push(`context=${context}`)
    return urlParameters.length > 0 ? urlParameters.join('&') : ''
  }

  private setupListeningToIframe() {
    addEventListener('message', (event) => {
      // console.log('event', event)
      if (this.isEventFromIframe(event)) {
        // const message = event.data
        // console.log('message', message)
        const eventData = this.getEventData(event)
        if (eventData) {
          this.handleEventFromIframe(eventData)
        }
      }
    })
  }

  private isEventFromIframe(event: MessageEvent<any>): boolean {
    // labs served by the server proxy are sandboxed with an opaque origin, so match on the sending window instead
    if (event.origin === 'null') {
      return !!event.source && event.source === this.getIFrame()?.contentWindow
    }
    return this.appUrl.startsWith(event.origin)
  }

  private getEventData(event: MessageEvent<any>): any {
    if (!event.data) {
      return null
    }
    if (typeof event.data === 'string') {
      try {
        return JSON.parse(event.data)
      } catch (e) {
        // apparently it's not a json string
      }
    }
    return event.data
  }

  private handleEventFromIframe(eventData: any) {
    const iframe = this.getIFrame()
    if (iframe) {
      const desiredHeight = this.getDesiredHeightFromEventData(eventData, true)
      if (desiredHeight) {
        // console.log(`setting height to ${desiredHeight}`, this._content?.title)
        const desiredHeightNumber = parseInt(desiredHeight)
        if (desiredHeightNumber < maxDesiredHeight) {
          this.setFixedHeight(desiredHeight)
        }
      }
    } else {
      const desiredHeight = this.getDesiredHeightFromEventData(eventData, false)
      if (desiredHeight) {
        console.warn(`Can't handle event data because the iframe cannot be found`, eventData)
      }
    }
  }

  private getDesiredHeightFromEventData(eventData: any, reportIgnoredEvent: boolean): string | undefined {
    if (eventData.event === 'resize') {
      const eventContentId = eventData.contentId ?? eventData.resourceId
      if (eventContentId === this.content?._id) {
        return eventData.height
      }
      return undefined
    } else if (eventData.topic === 'detectAngular' || eventData.isAngular) {
      // it is an angular debug event
      return undefined
    } else if (eventData.type === 'hello') {
      // concord labs are sending this event
      return undefined
    } else if (eventData.title?.startsWith('NetLogo Web:')) {
      console.log(`virtual biology net web event data`, eventData)
      if (eventData.height) {
        return eventData.height + 'px'
      }
      return undefined
    }
    if (reportIgnoredEvent) {
      console.log(`ignoring event data`, eventData)
    }
    return undefined
  }
}
